import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, MessageSquare, Paperclip, Send, Pencil, ChevronDown, ChevronRight, Clock, CheckCircle2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["todo","in_progress","blocked","review","done"];
const PRIORITIES = ["low","medium","high","critical"];
const STATUS_COLOR = {
  todo: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  blocked: "bg-rose-50 text-rose-700 border-rose-200",
  review: "bg-purple-50 text-purple-700 border-purple-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const PRIORITY_COLOR = { low: "text-emerald-600", medium: "text-amber-600", high: "text-orange-600", critical: "text-rose-600 font-semibold" };

const emptyForm = { title: "", category: "General", package: "", assigned_to: "", contractor_id: "", priority: "medium", status: "todo", due_date: "", start_date: "", progress: 0, description: "", depends_on: "" };

export default function WorkListTab({ pid, user, backendUrl }) {
  const [works, setWorks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");

  const canEdit = ["super_admin","director","architect","interior_designer","site_engineer","hr","purchase_manager"].includes(user?.role);

  const load = () => api.get(`/projects/${pid}/works`).then((r) => setWorks(r.data));
  useEffect(() => { load(); api.get("/employees").then(r => setEmployees(r.data)); api.get("/contractors").then(r => setContractors(r.data)).catch(()=>{}); /* eslint-disable-next-line */ }, [pid]);

  const empName = (id) => employees.find(e => e.id === id)?.name || "Unassigned";
  const contractorName = (id) => contractors.find(c => c.id === id)?.name || "";

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const payload = { ...form, depends_on: form.depends_on === "__none__" ? "" : form.depends_on, contractor_id: form.contractor_id === "__none__" ? "" : form.contractor_id };
    try { await api.post(`/projects/${pid}/works`, payload); toast.success("Work added"); setForm(emptyForm); setCreateOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const update = async (w, patch) => { await api.patch(`/projects/${pid}/works/${w.id}`, patch); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this work item?")) return; await api.delete(`/projects/${pid}/works/${id}`); load(); };

  const openEdit = (w) => {
    setEditForm({
      title: w.title || "", category: w.category || "General", package: w.package || "",
      assigned_to: w.assigned_to || "", contractor_id: w.contractor_id || "",
      priority: w.priority || "medium", status: w.status || "todo",
      due_date: w.due_date || "", start_date: w.start_date || "", progress: Number(w.progress||0),
      description: w.description || "", depends_on: w.depends_on || "",
    });
    setEditId(w.id); setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editForm.title.trim()) return toast.error("Title required");
    const payload = { ...editForm, depends_on: editForm.depends_on === "__none__" ? "" : editForm.depends_on, contractor_id: editForm.contractor_id === "__none__" ? "" : editForm.contractor_id };
    try { await api.patch(`/projects/${pid}/works/${editId}`, payload); setEditOpen(false); load(); toast.success("Work updated"); }
    catch { toast.error("Failed"); }
  };

  const categories = Array.from(new Set(works.map(w => w.category).filter(Boolean)));
  const filtered = works.filter(w =>
    (filterCategory === "all" || w.category === filterCategory) &&
    (filterStatus === "all" || w.status === filterStatus) &&
    (filterAssignee === "all" || w.assigned_to === filterAssignee)
  );

  const grouped = filtered.reduce((acc, w) => { const k = w.category || "Uncategorised"; (acc[k]=acc[k]||[]).push(w); return acc; }, {});

  return (
    <div className="space-y-4" data-testid="worklist-tab">
      <div className="rounded-2xl bg-card border border-border/60 p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Work List</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Categories, packages, assignees, comments and attachments — all persisted with audit log.</p>
        </div>
        {canEdit && <Button onClick={() => { setForm({ ...emptyForm }); setCreateOpen(true); }} data-testid="new-work-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" /> New Work</Button>}
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-card border border-border/60 p-4 grid grid-cols-1 md:grid-cols-3 gap-2">
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger data-testid="filter-work-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="filter-work-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger data-testid="filter-work-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignees</SelectItem>
            {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Works grouped by category */}
      <div className="space-y-3">
        {Object.entries(grouped).map(([cat, list]) => {
          const catProgress = Math.round(list.reduce((a,b) => a + (b.progress||0), 0) / list.length);
          return (
            <div key={cat} className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid={`work-category-${cat}`}>
              <div className="px-4 py-3 bg-muted/30 flex items-center justify-between">
                <div className="text-sm font-semibold flex items-center gap-2">{cat} <span className="text-[10px] bg-background border border-border px-1.5 py-0.5 rounded-full">{list.length}</span></div>
                <div className="text-xs text-muted-foreground">Avg progress: <span className="font-semibold text-foreground">{catProgress}%</span></div>
              </div>
              <div className="divide-y divide-border/50">
                {list.map(w => (
                  <WorkRow key={w.id} pid={pid} work={w} employees={employees} contractors={contractors} canEdit={canEdit}
                    expanded={expandedId===w.id} onToggle={() => setExpandedId(expandedId===w.id ? null : w.id)}
                    onUpdate={update} onEdit={openEdit} onRemove={remove} backendUrl={backendUrl} empName={empName} contractorName={contractorName} />
                ))}
              </div>
            </div>
          );
        })}
        {!filtered.length && <div className="p-10 text-center rounded-2xl border border-dashed border-border/60 text-sm text-muted-foreground">No work items match your filters.</div>}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Work Item</DialogTitle><DialogDescription>Add a work item to this project's Work List.</DialogDescription></DialogHeader>
          <WorkFormFields form={form} setForm={setForm} employees={employees} works={works} />
          <Button onClick={create} data-testid="save-work-btn" className="bg-primary text-primary-foreground">Save</Button>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Work</DialogTitle><DialogDescription>Update any field. Changes are recorded in the audit log.</DialogDescription></DialogHeader>
          <WorkFormFields form={editForm} setForm={setEditForm} employees={employees} works={works.filter(w => w.id !== editId)} />
          <Button onClick={saveEdit} data-testid="save-work-edit-btn" className="bg-primary text-primary-foreground">Update</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WorkFormFields({ form, setForm, employees, contractors, works }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Input className="col-span-2" placeholder="Work title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="work-title-input" />
      <Input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="work-category-input" />
      <Input placeholder="Package (optional)" value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })} />
      <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
        <SelectTrigger data-testid="work-assignee-select"><SelectValue placeholder="Employee assignee" /></SelectTrigger>
        <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={form.contractor_id} onValueChange={(v) => setForm({ ...form, contractor_id: v })}>
        <SelectTrigger data-testid="work-contractor-select"><SelectValue placeholder="Contractor (optional)" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— None —</SelectItem>
          {(contractors||[]).map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.trade ? ` · ${c.trade}` : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="date" placeholder="Start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="work-start-input" />
      <Input type="date" placeholder="Due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="work-due-input" />
      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" min="0" max="100" placeholder="Progress %" value={form.progress} onChange={(e) => setForm({ ...form, progress: Number(e.target.value) })} />
      <Select value={form.depends_on} onValueChange={(v) => setForm({ ...form, depends_on: v })}>
        <SelectTrigger className="col-span-2"><SelectValue placeholder="Depends on (optional)" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— None —</SelectItem>
          {works.map(w => <SelectItem key={w.id} value={w.id}>{w.title}</SelectItem>)}
        </SelectContent>
      </Select>
      <Textarea className="col-span-2" placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
    </div>
  );
}

function WorkRow({ pid, work: w, employees, contractors, canEdit, expanded, onToggle, onUpdate, onEdit, onRemove, backendUrl, empName, contractorName }) {
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!expanded) return;
    api.get(`/projects/${pid}/works/${w.id}/comments`).then(r => setComments(r.data));
    api.get(`/projects/${pid}/works/${w.id}/attachments`).then(r => setAttachments(r.data));
  }, [expanded, pid, w.id]);

  const addComment = async () => {
    if (!text.trim()) return;
    try { const { data } = await api.post(`/projects/${pid}/works/${w.id}/comments`, { text }); setComments([...comments, data]); setText(""); }
    catch { toast.error("Failed"); }
  };
  const attach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/projects/${pid}/works/${w.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments([...attachments, data]);
      toast.success("Attached");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const dueDate = w.due_date;
  const isOverdue = dueDate && new Date(dueDate) < new Date() && w.status !== "done";

  return (
    <div data-testid={`work-${w.id}`}>
      <div className="p-3 grid grid-cols-12 items-center gap-3">
        <button onClick={onToggle} className="col-span-5 flex items-start gap-2 text-left min-w-0">
          {expanded ? <ChevronDown className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate flex items-center gap-2">
              {w.title}
              {isOverdue && <span className="text-[9px] uppercase tracking-[0.15em] text-rose-600 font-semibold">Overdue</span>}
            </div>
            <div className="text-[11px] text-muted-foreground truncate flex items-center gap-2">
              {w.package && <span>{w.package}</span>}
              {w.description && <span>· {w.description.slice(0,60)}</span>}
            </div>
          </div>
        </button>
        <div className="col-span-2 flex items-center gap-2">
          <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs truncate">{empName(w.assigned_to)}{w.contractor_id ? ` · ${contractorName(w.contractor_id)}` : ""}</span>
        </div>
        <div className="col-span-1"><span className={`text-[11px] capitalize font-medium ${PRIORITY_COLOR[w.priority]||""}`}>{w.priority}</span></div>
        <div className="col-span-1 text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{dueDate || "—"}</div>
        <div className="col-span-2">
          <Select value={w.status} onValueChange={(v) => onUpdate(w, { status: v, progress: v === "done" ? 100 : w.progress })}>
            <SelectTrigger className={`h-7 text-[11px] capitalize ${STATUS_COLOR[w.status]||""}`} data-testid={`work-status-${w.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-1 flex justify-end gap-1">
          {canEdit && <button onClick={() => onEdit(w)} data-testid={`edit-work-${w.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>}
          {canEdit && <button onClick={() => onRemove(w.id)} data-testid={`delete-work-${w.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      </div>
      {/* Progress slider row */}
      <div className="px-3 pb-3 grid grid-cols-12 items-center gap-3">
        <div className="col-span-5" />
        <div className="col-span-6">
          <div className="flex items-center gap-2">
            <input type="range" min="0" max="100" step="5" value={w.progress||0}
              onChange={(e) => onUpdate(w, { progress: Number(e.target.value), status: Number(e.target.value) >= 100 ? "done" : w.status })}
              className="w-full accent-primary" data-testid={`work-progress-${w.id}`} />
            <span className="text-xs font-medium w-10 text-right">{w.progress||0}%</span>
          </div>
        </div>
        <div className="col-span-1 text-right text-[10px] text-muted-foreground flex items-center gap-2 justify-end">
          {expanded ? "" : <><MessageSquare className="w-3 h-3" />{" "}<Paperclip className="w-3 h-3" /></>}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/20 pt-2">
          {/* Comments */}
          <div className="rounded-lg bg-card border border-border/60 p-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Comments ({comments.length})</div>
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {comments.map(c => (
                <div key={c.id} className="text-xs">
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground"><span className="font-medium text-foreground">{c.user_name}</span><span>{new Date(c.created_at).toLocaleString()}</span></div>
                  <div>{c.text}</div>
                </div>
              ))}
              {!comments.length && <div className="text-xs text-muted-foreground italic">No comments yet.</div>}
            </div>
            <div className="flex gap-2 mt-2">
              <Input placeholder="Add a comment…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key==="Enter" && addComment()} data-testid={`work-comment-input-${w.id}`} className="h-8 text-xs" />
              <Button size="sm" onClick={addComment} data-testid={`work-comment-send-${w.id}`} className="bg-primary text-primary-foreground h-8"><Send className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          {/* Attachments */}
          <div className="rounded-lg bg-card border border-border/60 p-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5"><Paperclip className="w-3 h-3" /> Attachments ({attachments.length})</div>
            <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
              {attachments.map(a => (
                <div key={a.id} className="flex items-center justify-between text-xs bg-muted/40 rounded p-2">
                  <a href={`${backendUrl}/api/uploads/projects/${pid}/works/${w.id}/${a.file_name}`} target="_blank" rel="noreferrer" className="hover:underline truncate flex-1">{a.original_name}</a>
                  <span className="text-[10px] text-muted-foreground ml-2">{Math.round((a.size||0)/1024)} KB</span>
                </div>
              ))}
              {!attachments.length && <div className="text-xs text-muted-foreground italic">No attachments.</div>}
            </div>
            {canEdit && (
              <label className="mt-2 block">
                <input ref={fileRef} type="file" onChange={attach} className="hidden" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`work-attach-btn-${w.id}`}><Paperclip className="w-3.5 h-3.5 mr-1" />{uploading ? "Uploading…" : "Attach file"}</Button>
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
